import React, { useMemo, useState } from "react";
import { Wrapper } from "nystem-components";

const htmlEntities = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const decode = (text = "") =>
  text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (_, entity) => {
    if (entity[0] === "#") {
      const code =
        entity[1]?.toLowerCase() === "x"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      return Number.isFinite(code) ? String.fromCharCode(code) : _;
    }
    return htmlEntities[entity.toLowerCase()] || _;
  });

const toText = (html = "") =>
  decode(
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h\d)>/gi, "\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\n\s+/g, "\n")
      .trim(),
  );

const normalizeArea = ([x1, y1, x2, y2]) => ({
  x1: Math.min(x1, x2),
  y1: Math.min(y1, y2),
  x2: Math.max(x1, x2),
  y2: Math.max(y1, y2),
});

const parseHtmlBlocks = (html = "") => {
  const blocks = [];
  const blockRegex =
    /<(?<tag>div|h[1-6]|li)[^>]*data-bbox="(?<bbox>[^"]+)"[^>]*data-label="(?<label>[^"]+)"[^>]*>(?<content>[\s\S]*?)(?:<\/\k<tag>>|$)/gi;

  for (const match of html.matchAll(blockRegex)) {
    const bbox = match.groups.bbox.split(/\s+/).map(Number);
    if (!bbox.every(Number.isFinite)) continue;

    const text = toText(match.groups.content);
    if (!text) continue;

    blocks.push({
      area: normalizeArea(bbox),
      label: match.groups.label,
      text,
    });
  }

  return blocks;
};

const parseNodeBlocks = (node) => {
  const blocks = [];
  const stack = [node];

  while (stack.length) {
    const item = stack.pop();
    if (!item) continue;
    if (item.children) stack.push(...item.children);

    const bbox = item.bbox || [];
    const text = toText(item.html);
    if (
      !["Page", "Picture"].includes(item.block_type) &&
      text &&
      bbox.every(Number.isFinite)
    )
      blocks.push({
        area: normalizeArea(bbox),
        label: item.block_type || "Text",
        text,
      });
  }

  return blocks.reverse();
};

const parsePageBlocks = (page) =>
  (page?.json?.children || []).flatMap((child) => {
    const nodeBlocks = parseNodeBlocks(child);
    return nodeBlocks.length ? nodeBlocks : parseHtmlBlocks(child.html);
  });

const sourceSize = (page, image, blocks) => {
  const pageNode = page?.json?.children?.find(
    ({ block_type }) => block_type === "Page",
  );
  const bbox = pageNode?.bbox || page?.json?.children?.[0]?.bbox;

  if (bbox?.length === 4) {
    const { x1, y1, x2, y2 } = normalizeArea(bbox);
    return { x1, y1, width: x2 - x1 || 1, height: y2 - y1 || 1 };
  }

  return {
    x1: 0,
    y1: 0,
    width: Math.max(image?.width || 0, ...blocks.map(({ area }) => area.x2), 1),
    height: Math.max(
      image?.height || 0,
      ...blocks.map(({ area }) => area.y2),
      1,
    ),
  };
};

const parseOpenaiPages = (value) => {
  if (!value) return new Map();

  let data;
  try {
    data = JSON.parse(value);
  } catch {
    return new Map();
  }

  return new Map(
    (data.pages || []).map((page, index) => [
      page.reference?.imageId || page.reference?.index || index,
      page.blocks || [],
    ]),
  );
};

const DEFAULT_OPENAI_MULTIPLIERS = {
  x: 1,
  y: 2.25,
};

const normalizedOpenaiBlock = (block, multipliers) => {
  const position = block.position || {};
  const x = Number(position.x);
  const y = Number(position.y);
  const width = Number(position.width);
  const height = Number(position.height);
  const text = typeof block.text === "string" ? block.text.trim() : "";

  if (![x, y, width, height].every(Number.isFinite) || !text) return null;

  const left = x * multipliers.x;
  const top = y * multipliers.y;

  return {
    area: normalizeArea([left, top, left + width, top + height]),
    label: block.handwritten ? "OpenAI handwritten" : "OpenAI text",
    text,
  };
};

const normalizeOpenaiBlocks = (openaiBlocks, multipliers) => {
  return openaiBlocks
    .map((block) => normalizedOpenaiBlock(block, multipliers))
    .filter(
      (block) =>
        block &&
        block.area.x2 > block.area.x1 &&
        block.area.y2 > block.area.y1,
    );
};

const OPENAI_SOURCE = {
  x1: 0,
  y1: 0,
  width: 1000,
  height: 1000,
};

const parseDatalabPages = (value, images, labelPrefix = "") => {
  if (!value) return [];

  let data;
  try {
    data = JSON.parse(value);
  } catch {
    return [];
  }

  return (data.pages || [])
    .map((page, index) => {
      const imageIndex = page.reference?.index ?? index;
      const image =
        images.find(({ id }) => id === page.reference?.imageId) ||
        images[imageIndex];
      if (!image) return null;

      const blocks = parsePageBlocks(page)
        .filter(({ area }) => area.x2 > area.x1 && area.y2 > area.y1)
        .map((block) => ({
          ...block,
          label: labelPrefix ? `${labelPrefix} ${block.label}` : block.label,
        }));

      return {
        blocks,
        image,
        index: imageIndex,
        source: sourceSize(page, image, blocks),
      };
    })
    .filter(Boolean);
};

const pagesByImage = (pages) =>
  new Map(pages.flatMap((page) => [[page.image.id, page], [page.index, page]]));

const parsePages = (value, images, forcedValue, openaiValue, multipliers) => {
  const openaiPages = parseOpenaiPages(openaiValue);
  const datalabPages = parseDatalabPages(value, images);
  const forcedPages = pagesByImage(
    parseDatalabPages(forcedValue, images, "Datalab forced"),
  );

  return datalabPages.map(({ blocks, image, index, source }) => {
    const forced = forcedPages.get(image.id) || forcedPages.get(index);
    const openaiBlocks =
      openaiPages.get(image.id) || openaiPages.get(index) || [];
    const normalizedOpenaiBlocks = normalizeOpenaiBlocks(
      openaiBlocks,
      multipliers,
    );

    return {
      datalabBlocks: blocks,
      forcedBlocks: forced?.blocks || [],
      forcedSource: forced?.source || source,
      image,
      index,
      openaiBlocks: normalizedOpenaiBlocks,
      pageKey: image.id || index,
      source,
    };
  });
};

const imageUrl = (image) => `/image/upload/fullimage/${image.id}.${image.ext}`;

const OverlayLayer = ({ blocks, source }) => (
  <Wrapper className="absolute inset-0 pointer-events-none leading-none">
    {blocks.map((block, index) => (
      <Wrapper
        key={index}
        title={`${block.label}: ${block.text}`}
        style={blockStyle(block, source)}
      >
        <Wrapper style={blockTextStyle(block.label)}>
          {/Table/i.test(block.label) ? "" : block.text}
        </Wrapper>
      </Wrapper>
    ))}
  </Wrapper>
);

const PageImage = ({ image, layers }) => (
  <Wrapper className="relative inline-block max-w-full border border-gray-300 bg-white leading-none">
    <img
      src={imageUrl(image)}
      alt={image.name}
      className="block max-w-full h-auto"
    />
    {layers
      .filter(({ visible }) => visible)
      .map(({ blocks, id, source }) => (
        <OverlayLayer key={id} blocks={blocks} source={source} />
      ))}
  </Wrapper>
);

const LayerToggle = ({ checked, count, label, onChange }) => (
  <Wrapper
    renderAs="button"
    type="button"
    className={[
      "rounded",
      "border",
      "px-3",
      "py-1",
      "text-sm",
      "shadow-sm",
      checked
        ? "border-blue-600 bg-blue-500 text-white"
        : "border-gray-300 bg-gray-100 text-gray-900",
    ]}
    onClick={() => onChange(!checked)}
  >
    <Wrapper renderAs="span">{label}</Wrapper>
    <Wrapper
      renderAs="span"
      className={["ml-2", checked ? "text-blue-100" : "text-gray-500"]}
    >
      {count}
    </Wrapper>
  </Wrapper>
);

const MultiplierSlider = ({ label, max, min, onChange, step, value }) => (
  <Wrapper className="flex min-w-72 items-center gap-3 text-sm">
    <Wrapper renderAs="label" className="w-20 font-bold text-gray-900">
      {label}
    </Wrapper>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      className="h-2 w-48 cursor-pointer accent-blue-600"
      style={{ display: "block" }}
      onChange={(event) => onChange(Number(event.target.value))}
    />
    <Wrapper renderAs="code" className="min-w-12 text-gray-900">
      {value.toFixed(2)}
    </Wrapper>
  </Wrapper>
);

const blockStyle = ({ area, label }, source) => {
  const isForced = /Datalab forced/i.test(label);
  const isOpenai = /OpenAI/i.test(label);

  return {
    background: isOpenai
      ? "rgba(16, 185, 129, .16)"
      : isForced
        ? "rgba(249, 115, 22, .16)"
        : "rgba(255, 255, 0, .16)",
    boxSizing: "border-box",
    height: `${((area.y2 - area.y1) / source.height) * 100}%`,
    left: `${((area.x1 - source.x1) / source.width) * 100}%`,
    outline: isOpenai
      ? "1px solid rgba(5, 150, 105, .45)"
      : isForced
        ? "1px solid rgba(234, 88, 12, .45)"
        : "1px solid rgba(37, 99, 235, .35)",
    overflow: "visible",
    pointerEvents: "none",
    position: "absolute",
    top: `${((area.y1 - source.y1) / source.height) * 100}%`,
    width: `${((area.x2 - area.x1) / source.width) * 100}%`,
  };
};

const blockTextStyle = (label) => {
  const isHeader = /Header/i.test(label);
  const isList = /List/i.test(label);

  return {
    color: "#111827",
    fontFamily: "serif",
    fontSize: isHeader
      ? "clamp(16px, 2.1vw, 32px)"
      : isList
        ? "clamp(9px, .72vw, 13px)"
        : "clamp(10px, .9vw, 16px)",
    lineHeight: 1.05,
    maxHeight: "100%",
    minWidth: "100%",
    overflow: "hidden",
    position: "absolute",
    textShadow: "0 0 2px white, 0 0 3px white",
    whiteSpace: "pre-wrap",
  };
};

const TextDatalabPages = ({ model, value, view }) => {
  const [layerState, setLayerState] = useState({});
  const [showOpenaiPositionControls, setShowOpenaiPositionControls] =
    useState(false);
  const [openaiMultipliers, setOpenaiMultipliers] = useState(
    DEFAULT_OPENAI_MULTIPLIERS,
  );
  const images = view.value?.[model.imageField || "images"] || [];
  const forcedValue = view.value?.datalabForceOcr;
  const openaiValue = view.value?.openai;
  const pages = useMemo(
    () =>
      parsePages(value, images, forcedValue, openaiValue, openaiMultipliers),
    [forcedValue, images, openaiMultipliers, openaiValue, value],
  );

  if (!pages.length) return null;

  const isLayerVisible = (pageKey, layer) =>
    layerState[pageKey]?.[layer] ?? true;

  const setLayerVisible = (pageKey, layer, visible) => {
    setLayerState((state) => ({
      ...state,
      [pageKey]: {
        ...state[pageKey],
        [layer]: visible,
      },
    }));
  };

  return (
    <Wrapper className={["mt-6", model.className]}>
      {pages.map(
        (
          {
            datalabBlocks,
            forcedBlocks,
            forcedSource,
            image,
            openaiBlocks,
            pageKey,
            source,
          },
          pageIndex,
        ) => {
          const layers = [
            {
              blocks: datalabBlocks,
              id: "datalab",
              source,
              visible: isLayerVisible(pageKey, "datalab"),
            },
            forcedValue && {
              blocks: forcedBlocks,
              id: "forced",
              source: forcedSource,
              visible: isLayerVisible(pageKey, "forced"),
            },
            openaiValue && {
              blocks: openaiBlocks,
              id: "openai",
              source: OPENAI_SOURCE,
              visible: isLayerVisible(pageKey, "openai"),
            },
          ].filter(Boolean);

          return (
            <Wrapper key={image.id} className="my-6">
              <Wrapper className="font-bold mb-2 text-gray-900">
                OCR page {pageIndex + 1} of {pages.length}
              </Wrapper>
              <Wrapper className="sticky top-12 z-20 mb-3 flex flex-wrap items-center gap-1">
                <LayerToggle
                  checked={isLayerVisible(pageKey, "datalab")}
                  count={datalabBlocks.length}
                  label="Datalab"
                  onChange={(visible) =>
                    setLayerVisible(pageKey, "datalab", visible)
                  }
                />
                {forcedValue && (
                  <LayerToggle
                    checked={isLayerVisible(pageKey, "forced")}
                    count={forcedBlocks.length}
                    label="Datalab force OCR"
                    onChange={(visible) =>
                      setLayerVisible(pageKey, "forced", visible)
                    }
                  />
                )}
                {openaiValue && (
                  <LayerToggle
                    checked={isLayerVisible(pageKey, "openai")}
                    count={openaiBlocks.length}
                    label="OpenAI"
                    onChange={(visible) =>
                      setLayerVisible(pageKey, "openai", visible)
                    }
                  />
                )}
                {openaiValue && (
                  <Wrapper
                    renderAs="button"
                    type="button"
                    className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs text-gray-900 shadow-sm"
                    onClick={() =>
                      setShowOpenaiPositionControls((visible) => !visible)
                    }
                  >
                    Adjust pos
                  </Wrapper>
                )}
                {openaiValue && showOpenaiPositionControls && (
                  <Wrapper className="ml-2 flex flex-wrap items-center gap-4 rounded border border-blue-200 bg-white p-2 shadow-sm">
                    <MultiplierSlider
                      label="OpenAI x"
                      min={0.5}
                      max={3}
                      step={0.01}
                      value={openaiMultipliers.x}
                      onChange={(x) =>
                        setOpenaiMultipliers((multipliers) => ({
                          ...multipliers,
                          x,
                        }))
                      }
                    />
                    <MultiplierSlider
                      label="OpenAI y"
                      min={0.5}
                      max={4}
                      step={0.01}
                      value={openaiMultipliers.y}
                      onChange={(y) =>
                        setOpenaiMultipliers((multipliers) => ({
                          ...multipliers,
                          y,
                        }))
                      }
                    />
                  </Wrapper>
                )}
              </Wrapper>
              <PageImage image={image} layers={layers} />
            </Wrapper>
          );
        },
      )}
    </Wrapper>
  );
};

export default TextDatalabPages;
